import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Save } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { Button } from "@/components/ui/button.tsx"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"
import UnitSelector from "@/components/UnitSelector.tsx"
import type { KPIDefinition } from "@/types/kpiTypes.ts"

import { upsertKPI } from "../api/upsertKPI.ts"

import type { KPIUpdateRequest } from "../types/kpiEditorTypes.ts"

const formSchema = z.object({
  id: z.int().min(1),
  name: z.string().min(1).max(255),
  unitId: z.int().min(1),
})

export default function KPIEditor({
  id,
  value,
  onSave,
}: {
  id?: number
  value?: KPIDefinition
  onSave?: () => void
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: value,
  })

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: KPIUpdateRequest) => upsertKPI(data, id),
    onSuccess: () => {
      toast("KPI Updated")
      queryClient.invalidateQueries({ queryKey: ["kpis"] })
    },
  })

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    if (onSave) onSave()
    mutation.mutate(data)
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-3" onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ID</FormLabel>
              <FormControl>
                <Input
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  type="number"
                  step={1}
                  className="bg-white"
                />
              </FormControl>
              <FormDescription>Unique integer identifier for KPI</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} type="text" className="bg-white" />
              </FormControl>
              <FormDescription>Display name for KPI</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="unitId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Unit</FormLabel>
              <FormControl>
                <UnitSelector {...field} />
              </FormControl>
              <FormDescription>Unit for KPI value</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            <Save /> Save
          </Button>
        </div>
      </form>
    </Form>
  )
}
